import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/i18n/I18nProvider";
import { TIDS } from "@/constants/testIds";
import { createLead } from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const BookingDialog = ({ open, onOpenChange }) => {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createLead({
        name, email, phone: phone || null, company: company || null,
        message: message || null, source: "consultation",
      });
      toast.success(t.booking.success);
      setName(""); setEmail(""); setPhone(""); setCompany(""); setMessage("");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore. Riprova.");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={TIDS.bookingDialog} className="max-w-md sm:max-w-lg rounded-2xl p-0 overflow-hidden">
        <div className="p-6 md:p-8">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-semibold tracking-tight text-[#111111]">
              {t.booking.title}
            </DialogTitle>
            <DialogDescription className="text-[#6B7280]">{t.booking.subtitle}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t.booking.name} *</Label>
              <Input data-testid={TIDS.bookingName} value={name} onChange={(e) => setName(e.target.value)} required className="rounded-xl h-11 border-[#E5E7EB]" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t.booking.email} *</Label>
              <Input data-testid={TIDS.bookingEmail} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-xl h-11 border-[#E5E7EB]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t.booking.phone}</Label>
                <Input data-testid={TIDS.bookingPhone} value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-xl h-11 border-[#E5E7EB]" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t.booking.company}</Label>
                <Input data-testid={TIDS.bookingCompany} value={company} onChange={(e) => setCompany(e.target.value)} className="rounded-xl h-11 border-[#E5E7EB]" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t.booking.message}</Label>
              <Textarea data-testid={TIDS.bookingMessage} value={message} onChange={(e) => setMessage(e.target.value)} className="rounded-xl border-[#E5E7EB] min-h-[90px] resize-none" />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid={TIDS.bookingSubmit}
              className="btn-blue w-full justify-center disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {t.booking.submit}
            </button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDialog;
